import { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import Login from './Login';
import CalendarSidebar from './CalendarSidebar';
import LeadCard from './LeadCard';

function toDateKey(date) {
  return date.toISOString().split('T')[0];
}

// All possible statuses (from OUTCOMES + "New")
const STATUS_OPTIONS = [
  'All',
  'New',
  'Interested',
  'Not interested',
  'Ringing no response',
  'Wrong number',
  'Incorrect PoC',
  'Call back'
];

export default function App() {
  const [session,            setSession]            = useState(null);
  const [repRecord,          setRepRecord]          = useState(null);
  const [leads,              setLeads]              = useState([]);
  const [activeDates,        setActiveDates]        = useState([]);
  const [selectedDate,       setSelectedDate]       = useState(toDateKey(new Date()));
  const [filterStatus,       setFilterStatus]       = useState('All');
  const [loading,            setLoading]            = useState(true);
  const [authChecked,        setAuthChecked]        = useState(false);
  const [isFiltering,        setIsFiltering]        = useState(false); // prevent double fetch
  const [totals,             setTotals]             = useState(null);  // whole-book counts
  const [statsTick,          setStatsTick]          = useState(0);     // bump to refresh totals
  const [leadStates,         setLeadStates]         = useState([]);
  const [stateFilter,        setStateFilter]        = useState('');
  const [cityFilter,         setCityFilter]         = useState('');
  const [sectorFilter,       setSectorFilter]       = useState('');
  const [designationFilter,  setDesignationFilter]  = useState('');
  const [dueFromFilter,      setDueFromFilter]      = useState('');
  const [dueToFilter,        setDueToFilter]        = useState('');
  const [usingAdvancedFilters,setUsingAdvancedFilters] = useState(false);
  const [advancedTotalCount, setAdvancedTotalCount] = useState(null);
  const [phoneOnly,          setPhoneOnly]          = useState(false);

  // ── AUTH ─────────────────────────────────────────────────
  useEffect(() => {
    console.log('Checking initial session...');
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('Initial session:', session ? `User: ${session.user.email}` : 'None');
      setSession(session);
      setAuthChecked(true);
    }).catch(err => {
      console.error('Error getting session:', err);
      setAuthChecked(true);
    });
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log('Auth state changed:', _event, session ? `User: ${session.user.email}` : 'Session cleared');
      setSession(session);
    });
    
    return () => subscription.unsubscribe();
  }, []);

  // ── FETCH REP RECORD (match email → reps table) ──────────
  useEffect(() => {
    if (!session) return;
    const email = session.user.email;
    if (!email.endsWith('@dataneers.in')) {
      supabase.auth.signOut();
      alert('Only @dataneers.in accounts are permitted.');
      return;
    }
    console.log('Fetching rep record for email:', email);
    supabase.from('reps').select('*').eq('email', email).single()
      .then(({ data, error }) => {
        if (error) {
          console.error('Error fetching rep record:', error);
        } else if (!data) {
          console.error('Rep not found for email:', email);
        } else {
          console.log('Rep record loaded:', data);
          setRepRecord(data);
        }
      });
  }, [session]);

  // ── FETCH ACTIVE DATES (dots on calendar) ────────────────
  useEffect(() => {
    if (!repRecord) return;
    supabase
      .from('leads')
      .select('call_due_date')
      .eq('assigned_rep_id', repRecord.id)
      .not('call_due_date', 'is', null)
      .then(({ data }) => {
        if (!data) return;
        const dates = [...new Set(data.map(l => l.call_due_date))];
        setActiveDates(dates);
      });
  }, [repRecord]);

  // ── FETCH AVAILABLE STATES FOR FILTER BAR ─────────────────
  useEffect(() => {
    if (!repRecord) return;

    let cancelled = false;

    supabase
      .rpc('lead_states', { rep: repRecord.id })
      .then(({ data, error }) => {
        if (error) {
          console.error('Error fetching lead states:', error);
          return;
        }
        if (!cancelled && Array.isArray(data)) {
          const uniqueStates = [...new Set(data.map(row => row.state).filter(Boolean))];
          uniqueStates.sort((a, b) => a.localeCompare(b));
          setLeadStates(uniqueStates);
        }
      })
      .catch(err => {
        console.error('Error in lead_states RPC:', err);
      });

    return () => { cancelled = true; };
  }, [repRecord]);

  // ── FETCH WHOLE-BOOK TOTALS (not date-scoped) ────────────
  // The sidebar stats below only describe the selected day. These are the
  // rep's entire assigned book, so a 100-a-day queue doesn't read as
  // "I only have 100 leads".
  useEffect(() => {
    if (!repRecord) return;
    let cancelled = false;

    const countOf = () => supabase
      .from('leads')
      .select('id', { count: 'exact', head: true })
      .eq('assigned_rep_id', repRecord.id);

    Promise.all([
      countOf(),
      countOf().neq('status', 'New'),
      countOf().eq('status', 'Interested'),
      countOf().not('phone', 'is', null).neq('phone', ''),
    ]).then(([all, worked, interested, callable]) => {
      if (cancelled) return;
      setTotals({
        all:        all.count        ?? 0,
        worked:     worked.count     ?? 0,
        interested: interested.count ?? 0,
        callable:   callable.count   ?? 0,
      });
    }).catch(err => console.error('Error fetching totals:', err));

    return () => { cancelled = true; };
  }, [repRecord, statsTick]);

  // ── FETCH LEADS FOR SELECTED DATE + STATUS FILTER ────────
  const fetchLeads = useCallback(async (dateKey, statusFilter) => {
    if (!repRecord) return;
    setLoading(true);
    let query = supabase
      .from('leads')
      .select(`*, call_logs(id, outcome, notes, called_at)`)
      .eq('assigned_rep_id', repRecord.id)
      .eq('call_due_date', dateKey)
      .order('ils_score', { ascending: false });

    if (statusFilter && statusFilter !== 'All') {
      query = query.eq('status', statusFilter);
    }

    const { data, error } = await query;
    if (error) console.error(error);
    else setLeads(data || []);
    setLoading(false);
  }, [repRecord]);

  // Called when date changes (calendar click) – auto-apply current status filter
  useEffect(() => {
    if (repRecord && !isFiltering && !usingAdvancedFilters) {
      fetchLeads(selectedDate, filterStatus);
    }
  }, [repRecord, selectedDate, filterStatus, fetchLeads, isFiltering, usingAdvancedFilters]);

  // "Go" button handler – explicitly fetch with current date and status
  const handleGoFilter = () => {
    setIsFiltering(true);

    if (usingAdvancedFilters) {
      // Clear advanced filters and return to calendar/status behaviour
      setStateFilter('');
      setCityFilter('');
      setSectorFilter('');
      setDesignationFilter('');
      setDueFromFilter('');
      setDueToFilter('');
      setUsingAdvancedFilters(false);
      setAdvancedTotalCount(null);
      setPhoneOnly(false);
    }

    fetchLeads(selectedDate, filterStatus).finally(() => setIsFiltering(false));
  };

  // ── ADVANCED FILTERS (state, city, sector, etc.) ──────────
  async function applyAdvancedFilters() {
    if (!repRecord) return;

    setLoading(true);

    let query = supabase
      .from('leads')
      .select(`*, call_logs(id, outcome, notes, called_at)`, { count: 'exact' })
      .eq('assigned_rep_id', repRecord.id)
      .order('ils_score', { ascending: false });

    if (stateFilter) {
      query = query.eq('state', stateFilter);
    }
    const city = cityFilter.trim();
    if (city) {
      query = query.ilike('city', `%${city}%`);
    }
    const sector = sectorFilter.trim();
    if (sector) {
      query = query.ilike('industry', `%${sector}%`);
    }
    const designation = designationFilter.trim();
    if (designation) {
      query = query.ilike('designation', `%${designation}%`);
    }
    if (dueFromFilter) {
      query = query.gte('call_due_date', dueFromFilter);
    }
    if (dueToFilter) {
      query = query.lte('call_due_date', dueToFilter);
    }

    if (phoneOnly) {
      query = query.not('phone', 'is', null).neq('phone', '');
    }

    query = query.range(0, 499);

    const { data, error, count } = await query;
    if (error) {
      console.error('Error fetching filtered leads:', error);
      setAdvancedTotalCount(null);
    } else {
      setLeads(data || []);
      setAdvancedTotalCount(typeof count === 'number' ? count : null);
    }
    setLoading(false);
  }

  function handleApplyFilters() {
    const hasFilters = !!(
      stateFilter ||
      cityFilter.trim() ||
      sectorFilter.trim() ||
      designationFilter.trim() ||
      dueFromFilter ||
      dueToFilter ||
      phoneOnly
    );

    if (!repRecord) return;

    if (hasFilters) {
      setUsingAdvancedFilters(true);
      applyAdvancedFilters();
    } else {
      // No filters – behave like reset to calendar/status behaviour
      if (usingAdvancedFilters) {
        setUsingAdvancedFilters(false);
      }
      setAdvancedTotalCount(null);
      fetchLeads(selectedDate, filterStatus);
    }
  }

  function handleResetFilters() {
    setStateFilter('');
    setCityFilter('');
    setSectorFilter('');
    setDesignationFilter('');
    setDueFromFilter('');
    setDueToFilter('');
    setUsingAdvancedFilters(false);
    setAdvancedTotalCount(null);
    setPhoneOnly(false);
    fetchLeads(selectedDate, filterStatus);
  }

  // ── STATUS UPDATE (optimistic) ────────────────────────────
  function handleStatusChange(leadId, newStatus) {
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
    setStatsTick(t => t + 1); // keep the book totals honest as he works
  }

  // ── STATS FOR SIDEBAR (still useful) ─────────────────────
  const stats = {
    due:        leads.length,
    called:     leads.filter(l => l.status !== 'New').length,
    remaining:  leads.filter(l => l.status === 'New').length,
    interested: leads.filter(l => l.status === 'Interested').length,
  };

  // ── SIGN OUT ─────────────────────────────────────────────
  async function signOut() {
    await supabase.auth.signOut();
    setSession(null);
    setRepRecord(null);
    setLeads([]);
  }

  // ── RENDER ────────────────────────────────────────────────
  if (!authChecked) return null;
  if (!session)     return <Login />;

  const isToday = selectedDate === toDateKey(new Date());
  const dateLabel = isToday
    ? 'Today — ' + new Date().toLocaleDateString('en-IN', { weekday:'short', day:'numeric', month:'short', year:'numeric' })
    : new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-IN', { weekday:'short', day:'numeric', month:'short', year:'numeric' });

  return (
    <div style={s.root}>
      {/* Top bar (unchanged) */}
      <div style={s.topbar}>
        <div style={s.logoWrap}>
          <span style={s.logo}>Dataneers</span>
          <span style={s.logoDot}>·</span>
          <span style={s.logoSub}>Sales</span>
        </div>
        <div style={s.repPill}>
          <div style={s.avatar}>
            {repRecord ? repRecord.name.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase() : '..'}
          </div>
          <span style={s.repName}>{repRecord?.name || session.user.email}</span>
          <button style={s.signOutBtn} onClick={signOut}>Sign out</button>
        </div>
      </div>

      {/* Main layout */}
      <div style={s.main}>
        <CalendarSidebar
          activeDates={activeDates}
          selectedDate={selectedDate}
          onSelectDate={(dateKey) => {
            if (usingAdvancedFilters) {
              setStateFilter('');
              setCityFilter('');
              setSectorFilter('');
              setDesignationFilter('');
              setDueFromFilter('');
              setDueToFilter('');
              setUsingAdvancedFilters(false);
              setAdvancedTotalCount(null);
              setPhoneOnly(false);
            }
            setSelectedDate(dateKey);
            fetchLeads(dateKey, filterStatus);
          }}
          stats={stats}
        />

        <div style={s.content}>
          <div style={s.contentHeader}>
            <div>
              <div style={s.dateTitle}>{dateLabel}</div>
              <div style={s.dateSub}>
                {usingAdvancedFilters ? (
                  <>
                    {advancedTotalCount != null
                      ? `${advancedTotalCount} matching lead${advancedTotalCount !== 1 ? 's' : ''}`
                      : `${leads.length} matching lead${leads.length !== 1 ? 's' : ''}`}
                    {totals ? ` · ${totals.all.toLocaleString('en-IN')} assigned to you in total` : ''}
                  </>
                ) : (
                  <>
                    {leads.length} lead{leads.length !== 1 ? 's' : ''} due today
                    {totals ? ` · ${totals.all.toLocaleString('en-IN')} assigned to you in total` : ''}
                  </>
                )}
              </div>
            </div>

            {/* Status Filter + Go Button */}
            <div style={s.filterBar}>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={s.statusSelect}
              >
                {STATUS_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              <button onClick={handleGoFilter} style={s.goButton}>
                Go
              </button>
            </div>
          </div>

          {/* Whole book — so the daily slice reads as part of something bigger */}
          {totals && (
            <div style={s.book}>
              <div style={s.bookRow}>
                <div style={s.bookItem}>
                  <div style={s.bookNum}>{totals.all.toLocaleString('en-IN')}</div>
                  <div style={s.bookCap}>Your leads</div>
                </div>
                <div style={s.bookItem}>
                  <div style={s.bookNum}>{totals.callable.toLocaleString('en-IN')}</div>
                  <div style={s.bookCap}>With a phone number</div>
                </div>
                <div style={s.bookItem}>
                  <div style={s.bookNum}>{totals.worked.toLocaleString('en-IN')}</div>
                  <div style={s.bookCap}>Worked so far</div>
                </div>
                <div style={s.bookItem}>
                  <div style={{...s.bookNum, color:'#047857'}}>
                    {totals.interested.toLocaleString('en-IN')}
                  </div>
                  <div style={s.bookCap}>Interested</div>
                </div>
              </div>
              <div style={s.barTrack}>
                <div style={{
                  ...s.barFill,
                  width: totals.all ? `${Math.min(100, (totals.worked / totals.all) * 100)}%` : '0%'
                }} />
              </div>
              <div style={s.bookFoot}>
                {(totals.all - totals.worked).toLocaleString('en-IN')} still to work through
              </div>
            </div>
          )}

          {/* Advanced filter bar (optional) */}
          <div style={s.advancedFilterBar}>
            <select
              value={stateFilter}
              onChange={e => setStateFilter(e.target.value)}
              style={s.filterSelect}
            >
              <option value="">All states</option>
              {leadStates.map(st => (
                <option key={st} value={st}>{st}</option>
              ))}
            </select>

            <input
              type="text"
              placeholder="City"
              value={cityFilter}
              onChange={e => setCityFilter(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleApplyFilters(); }}
              style={s.filterInput}
            />
            <input
              type="text"
              placeholder="Sector"
              value={sectorFilter}
              onChange={e => setSectorFilter(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleApplyFilters(); }}
              style={s.filterInput}
            />
            <input
              type="text"
              placeholder="Designation"
              value={designationFilter}
              onChange={e => setDesignationFilter(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleApplyFilters(); }}
              style={s.filterInput}
            />

            <input
              type="date"
              value={dueFromFilter}
              onChange={e => setDueFromFilter(e.target.value)}
              style={s.filterDate}
            />
            <span style={s.filterDateSeparator}>to</span>
            <input
              type="date"
              value={dueToFilter}
              onChange={e => setDueToFilter(e.target.value)}
              style={s.filterDate}
            />

            <label style={{ display:'flex', alignItems:'center', gap:4, fontSize:12, color:'#111827' }}>
              <input
                type="checkbox"
                checked={phoneOnly}
                onChange={e => setPhoneOnly(e.target.checked)}
                style={{ margin:0 }}
              />
              Has phone number
            </label>

            <button onClick={handleApplyFilters} style={s.filterApplyButton}>
              Apply
            </button>
            <button onClick={handleResetFilters} style={s.filterResetButton}>
              Reset
            </button>

            {usingAdvancedFilters && (
              <span style={s.filterCount}>
                {advancedTotalCount != null
                  ? `${Math.min(500, advancedTotalCount)} of ${advancedTotalCount} matches`
                  : `${leads.length} match${leads.length !== 1 ? 'es' : ''}`}
                {advancedTotalCount != null && advancedTotalCount > 500
                  ? ' · Showing 500 of ' + advancedTotalCount + '. Narrow your filters.'
                  : ''}
              </span>
            )}
          </div>

          {/* Lead list */}
          {loading ? (
            <div style={s.empty}>Loading leads...</div>
          ) : leads.length === 0 ? (
            <div style={s.empty}>
              {usingAdvancedFilters ? (
                <>
                  <div style={s.emptyTitle}>No leads match these filters</div>
                  <div style={s.emptySub}>Adjust filters or Reset to go back to calendar view</div>
                </>
              ) : (
                <>
                  <div style={s.emptyTitle}>No leads due on this date</div>
                  <div style={s.emptySub}>Select another date or change status filter</div>
                </>
              )}
            </div>
          ) : (
            leads.map(lead => (
              <LeadCard
                key={lead.id}
                lead={lead}
                repId={repRecord?.id}
                repEmail={session.user.email} // pass rep's email for mailto
                onStatusChange={handleStatusChange}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  root:          { minHeight:'100vh', background:'#F9FAFB', fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif' },
  topbar:        { background:'#fff', borderBottom:'0.5px solid #E5E7EB', padding:'12px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:10 },
  logoWrap:      { display:'flex', alignItems:'center', gap:6 },
  logo:          { fontSize:14, fontWeight:600, color:'#1B3A5C' },
  logoDot:       { color:'#D1D5DB' },
  logoSub:       { fontSize:14, color:'#6B7280' },
  repPill:       { display:'flex', alignItems:'center', gap:10 },
  avatar:        { width:28, height:28, borderRadius:'50%', background:'#DBEAFE', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:500, color:'#1B3A5C' },
  repName:       { fontSize:13, color:'#6B7280' },
  signOutBtn:    { fontSize:12, padding:'4px 10px', border:'0.5px solid #E5E7EB', borderRadius:6, background:'transparent', cursor:'pointer', color:'#9CA3AF' },
  main:          { display:'flex', minHeight:'calc(100vh - 49px)' },
  content:       { flex:1, padding:20, minWidth:0 },
  contentHeader:     { marginBottom:14, display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 },
  dateTitle:     { fontSize:16, fontWeight:500, color:'#111827' },
  dateSub:       { fontSize:12, color:'#9CA3AF', marginTop:3 },
  filterBar:         { display:'flex', gap:8, alignItems:'center' },
  book:          { background:'#fff', border:'0.5px solid #E5E7EB', borderRadius:10, padding:'14px 16px', marginBottom:16 },
  bookRow:       { display:'flex', gap:28, flexWrap:'wrap' },
  bookItem:      { minWidth:96 },
  bookNum:       { fontSize:20, fontWeight:600, color:'#111827', fontVariantNumeric:'tabular-nums', lineHeight:1.2 },
  bookCap:       { fontSize:11.5, color:'#9CA3AF', marginTop:2 },
  barTrack:      { height:4, background:'#F3F4F6', borderRadius:2, overflow:'hidden', marginTop:14 },
  barFill:           { height:'100%', background:'#1B3A5C', borderRadius:2, transition:'width .2s' },
  bookFoot:          { fontSize:11.5, color:'#9CA3AF', marginTop:7 },
  statusSelect:      { padding:'6px 10px', borderRadius:8, border:'0.5px solid #E5E7EB', background:'#fff', fontSize:12, color:'#111827' },
  goButton:          { padding:'6px 14px', borderRadius:8, border:'none', background:'#1B3A5C', color:'#fff', fontSize:12, cursor:'pointer', fontWeight:500 },
  advancedFilterBar: { display:'flex', flexWrap:'wrap', gap:8, alignItems:'center', marginBottom:12 },
  filterSelect:      { padding:'6px 10px', borderRadius:8, border:'0.5px solid #E5E7EB', background:'#fff', fontSize:12, color:'#111827', minWidth:120 },
  filterInput:       { padding:'6px 10px', borderRadius:8, border:'0.5px solid #E5E7EB', background:'#fff', fontSize:12, color:'#111827', minWidth:110 },
  filterDate:        { padding:'6px 10px', borderRadius:8, border:'0.5px solid #E5E7EB', background:'#fff', fontSize:12, color:'#111827' },
  filterDateSeparator:{ fontSize:12, color:'#6B7280' },
  filterApplyButton: { padding:'6px 12px', borderRadius:8, border:'none', background:'#1B3A5C', color:'#fff', fontSize:12, cursor:'pointer', fontWeight:500 },
  filterResetButton: { padding:'6px 10px', borderRadius:8, border:'0.5px solid #E5E7EB', background:'#fff', fontSize:12, cursor:'pointer', color:'#374151' },
  filterCount:       { fontSize:12, color:'#6B7280', marginLeft:'auto' },
  empty:         { textAlign:'center', padding:'60px 20px' },
  emptyTitle:    { fontSize:15, fontWeight:500, color:'#374151', marginBottom:6 },
  emptySub:      { fontSize:13, color:'#9CA3AF' },
};